import React, { useState, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { useLanguage } from '../../../../../../../contexts/LanguageContext';
import { useGetBulletinFileListQuery } from '../../../../../../../api/endpoints/bulletinDocument';
import toastService from '../../../../../../../utils/toastService';
import { getEnv } from '../../../../../../../config/runtimeConfig';

const BulletinDocumentsTable = ({ bulletNo }) => {
    const { th } = useLanguage();

    const bulletId = useMemo(() => {
        if (!bulletNo) return null;
        const parsed = parseInt(bulletNo, 10);
        return isNaN(parsed) ? bulletNo.replace(/^0+/, '') || '0' : String(parsed);
    }, [bulletNo]);

    const [lazyParams, setLazyParams] = useState({
        rows: 10,
        sortField: null,
        sortOrder: null
    });

    const { data: bulletinFiles, isLoading } = useGetBulletinFileListQuery(bulletId, {
        skip: !bulletId
    });

    const fileList = useMemo(() => {
        if (!bulletinFiles) return [];
        if (Array.isArray(bulletinFiles)) return bulletinFiles;
        if (bulletinFiles?.data && Array.isArray(bulletinFiles.data)) return bulletinFiles.data;
        return [];
    }, [bulletinFiles]);

    const sortedData = useMemo(() => {
        const allItems = fileList;

        if (!lazyParams.sortField || lazyParams.sortOrder === 0) {
            return allItems;
        }

        return [...allItems].sort((a, b) => {
            let aValue = a[lazyParams.sortField];
            let bValue = b[lazyParams.sortField];

            if (aValue == null && bValue == null) return 0;
            if (aValue == null) return lazyParams.sortOrder;
            if (bValue == null) return -lazyParams.sortOrder;

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }

            if (aValue < bValue) return -lazyParams.sortOrder;
            if (aValue > bValue) return lazyParams.sortOrder;
            return 0;
        });
    }, [fileList, lazyParams.sortField, lazyParams.sortOrder]);

    const onSort = (event) => {
        setLazyParams((prev) => ({
            ...prev,
            sortField: event.sortField,
            sortOrder: event.sortOrder
        }));
    };

    const handleDownload = async (rowData) => {
        try {
            const downloadUrl = rowData.downloadUrl;
            if (!downloadUrl) {
                toastService.error(th('warrantyClaims.bulletinDocuments.downloadError'));
                return;
            }

            const baseUrl = getEnv('REACT_APP_API_URL')?.replace(/\/$/, '') || '';
            const url = `${baseUrl}${downloadUrl}`;

            const response = await fetch(url, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Accept': 'application/octet-stream'
                }
            });

            if (!response.ok) {
                const contentType = response.headers.get('content-type');
                if (contentType && contentType.includes('application/json')) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || errorData.errorMessage || 'Download failed');
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const blob = await response.blob();

            if (blob.size === 0) {
                toastService.warn(th('warrantyClaims.bulletinDocuments.emptyFile'));
                return;
            }

            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;

            const contentDisposition = response.headers.get('content-disposition');
            let fileName = rowData.fileName || 'download';
            if (contentDisposition) {
                const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
                if (matches != null && matches[1]) {
                    fileName = matches[1].replace(/['"]/g, '');
                }
            }

            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);

            toastService.success(th('warrantyClaims.bulletinDocuments.downloadSuccess'));
        } catch (error) {
            console.error('Bulletin file download error:', error);
            toastService.error(th('warrantyClaims.bulletinDocuments.downloadError'));
        }
    };

    const actionBodyTemplate = (rowData) => (
        <div className="flex gap-2 justify-content-center">
            <Button
                icon="pi pi-download"
                className="p-button-rounded p-button-text p-button-sm"
                onClick={() => handleDownload(rowData)}
                tooltip={th('warrantyClaims.bulletinDocuments.download')}
                tooltipOptions={{ position: 'top' }}
            />
        </div>
    );

    if (!bulletId) {
        return (
            <div className="p-3 text-center text-500">
                {th('warrantyClaims.bulletinDocuments.noBulletinNo')}
            </div>
        );
    }

    return (
        <div>
            <DataTable
                value={sortedData}
                className="p-datatable-gridlines mt-3"
                showGridlines
                paginator
                rows={lazyParams.rows}
                onSort={onSort}
                sortField={lazyParams.sortField}
                sortOrder={lazyParams.sortOrder}
                dataKey="fileName"
                loading={isLoading}
                emptyMessage={th('warrantyClaims.bulletinDocuments.emptyMessage')}
                rowsPerPageOptions={[5, 10, 25, 50]}
                tableStyle={{ minWidth: '40rem' }}
            >
                <Column
                    body={actionBodyTemplate}
                    style={{ width: '5%', minWidth: '60px' }}
                    bodyClassName="text-center"
                    headerClassName="text-center"
                    exportable={false}
                />
                <Column
                    field="fileName"
                    header={th('warrantyClaims.bulletinDocuments.fileName')}
                    sortable
                />
                <Column
                    field="fileType"
                    header={th('warrantyClaims.bulletinDocuments.fileType')}
                    sortable
                    style={{ width: '15%' }}
                />
            </DataTable>
        </div>
    );
};

export default BulletinDocumentsTable;
